import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
  ProgressBar,
  Badge,
  ListGroup,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaShieldAlt,
  FaIdCard,
  FaKey,
  FaFingerprint,
  FaInfoCircle,
  FaPlug,
  FaDownload,
  FaRedo,
} from "react-icons/fa";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import BiometricService from "../../../services/BiometricService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";

const VerifyAadhaar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);
  const [aadhaarConsent, setAadhaarConsent] = useState("");
  const [formData, setFormData] = useState({
    aadhaar_number: "",
    aadhaar_consent: false,
    verification_type: "otp",
  });
  const [errors, setErrors] = useState({});
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [verificationInProgress, setVerificationInProgress] = useState(false);

  // Biometric states
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState({
    loading: false,
    error: null,
    deviceInfo: null,
    captureData: null,
  });
  const [biometricProgress, setBiometricProgress] = useState({
    step: 0,
    message: "",
    percentage: 0,
  });
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);

  // Fetch Aadhaar consent on component mount
  useEffect(() => {
    fetchAadhaarConsent();
  }, []);

  // Handle OTP timer
  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpTimer]);

  // Fetch Aadhaar consent from backend
  const fetchAadhaarConsent = async () => {
    try {
      setConsentLoading(true);
      const result = await PPIDmtService.feetchConsent();

      if (!result.success) {
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setAadhaarConsent(res.data.consent_text || "");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setConsentLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let formattedValue = value;

    if (name === "aadhaar_number") {
      // Remove non-numeric characters and limit to 12 digits
      formattedValue = value.replace(/\D/g, "").substring(0, 12);
      // Add spaces for better readability (XXXX XXXX XXXX)
      if (formattedValue.length > 8) {
        formattedValue = formattedValue.replace(
          /(\d{4})(\d{4})(\d{0,4})/,
          "$1 $2 $3"
        );
      } else if (formattedValue.length > 4) {
        formattedValue = formattedValue.replace(/(\d{4})(\d{0,4})/, "$1 $2");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle verification type change
  const handleVerificationTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      verification_type: type,
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Aadhaar number validation
    const cleanAadhaar = formData.aadhaar_number.replace(/\s/g, "");
    if (!cleanAadhaar.trim()) {
      newErrors.aadhaar_number = "Aadhaar number is required";
    } else if (cleanAadhaar.length !== 12) {
      newErrors.aadhaar_number = "Aadhaar number must be 12 digits";
    } else if (!/^\d{12}$/.test(cleanAadhaar)) {
      newErrors.aadhaar_number = "Invalid Aadhaar number format";
    }

    // Consent validation
    if (!formData.aadhaar_consent) {
      newErrors.aadhaar_consent = "You must agree to the Aadhaar consent";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Show consent modal
  const handleShowConsent = () => {
    setShowConsentModal(true);
  };

  // Handle Aadhaar verification
  const handleVerifyAadhaar = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix all errors before verifying",
        timer: 2000,
      });
      return;
    }

    const cleanAadhaar = formData.aadhaar_number.replace(/\s/g, "");

    if (formData.verification_type === "otp") {
      // Generate Aadhaar OTP
      await generateAadhaarOtp(cleanAadhaar);
    } else if (formData.verification_type === "biometric") {
      // Show biometric modal
      setShowBiometricModal(true);
      // Start device check
      checkBiometricDevice();
    }
  };

  // Check biometric device availability
  const checkBiometricDevice = async () => {
    setBiometricStatus((prev) => ({ ...prev, loading: true, error: null }));
    updateBiometricProgress(1, "Checking biometric device...", 10);

    try {
      const deviceCheck = await BiometricService.checkServiceStatus();

      if (!deviceCheck.serviceAvailable) {
        throw new Error(deviceCheck.error || "Biometric device not found");
      }

      updateBiometricProgress(2, "Device detected successfully", 30);
      setBiometricStatus((prev) => ({
        ...prev,
        deviceInfo: deviceCheck.deviceInfo,
        loading: false,
      }));
    } catch (error) {
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  // Update biometric progress
  const updateBiometricProgress = (step, message, percentage) => {
    setBiometricProgress({
      step,
      message,
      percentage,
    });
  };

  // Capture biometric data
  const handleBiometricCapture = async () => {
    setBiometricStatus((prev) => ({ ...prev, loading: true, error: null }));
    updateBiometricProgress(3, "Ready to capture fingerprint...", 50);

    try {
      const captureResult = await BiometricService.captureBiometric({
        aadhaarNumber: formData.aadhaar_number.replace(/\s/g, ""),
      });

      if (!captureResult.success) {
        throw new Error(captureResult.error || "Biometric capture failed");
      }

      updateBiometricProgress(4, "Fingerprint captured successfully", 80);
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        captureData: captureResult.data,
      }));

      // console.log(captureResult);

      // Convert XML to base64 and send to backend
      await sendBiometricToBackend(captureResult.data);
      setShowBiometricModal(false);
    } catch (error) {
      setBiometricStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  // Send biometric data to backend
  const sendBiometricToBackend = async (biometricData) => {
    //   updateBiometricProgress(5, "Verifying with Aadhaar server...", 90);
    setLoading(true);

    try {
      // Convert XML response to base64 string
      const xmlString = BiometricService.convertXmlToBase64(
        biometricData.xmlResponse
      );

      const aadhaar_number = formData.aadhaar_number.replace(/\s/g, "");

      const result = await PPIDmtService.biometricKyc(
        data?.token,
        data?.wallet_ac_application_number,
        customerMobile,
        aadhaar_number,
        "12.9715987",
        "77.594566",
        xmlString
      );

      if (!result.success) {
        throw new Error(result.message || "Biometric verification failed");
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        // updateBiometricProgress(6, "Verification successful!", 100);

        Swal.fire({
          icon: "success",
          title: "Aadhaar Verified!",
          text: res.message || "Biometric verification successful",
          timer: 2000,
        }).then(() => {
          navigate("/ppi-money-transfer/verify-pan", {
            state: {
              customerMobile,
              data,
              aadhaarVerified: true,
              biometricVerified: true,
              verificationData: res.data,
            },
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Biometric Verification Failed",
        });
      }
    } catch (error) {
      //   setBiometricStatus((prev) => ({
      //     ...prev,
      //     error: error.message,
      //   }));
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Retry biometric capture
  const handleRetryBiometric = async () => {
    setBiometricStatus((prev) => ({ ...prev, error: null, captureData: null }));
    await handleBiometricCapture();
  };

  // Show troubleshooting guide
  const handleShowTroubleshooting = () => {
    const guide = BiometricService.getTroubleshootingGuide();
    setShowTroubleshoot(true);
  };

  // Reset biometric states
  const resetBiometric = () => {
    setBiometricStatus({
      loading: false,
      error: null,
      deviceInfo: null,
      captureData: null,
    });
    setBiometricProgress({
      step: 0,
      message: "",
      percentage: 0,
    });
  };

  // Generate Aadhaar OTP
  const generateAadhaarOtp = async (aadhaarNumber) => {
    setLoading(true);
    try {
      const result = await PPIDmtService.generateAadhaarOtp(
        data?.token,
        data?.wallet_ac_application_number,
        aadhaarNumber
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "Failed to generate OTP",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setOtpTimer(300); // 5 minutes timer
        setShowOtpModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res.message || "Could not generate OTP",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Verify Aadhaar OTP
  const verifyAadhaarOtp = async () => {
    if (!otp || otp.length !== 6) {
      Swal.fire({
        icon: "warning",
        title: "Invalid OTP",
        text: "Please enter a valid 6-digit OTP",
      });
      return;
    }

    setVerificationInProgress(true);
    try {
      const result = await PPIDmtService.verifyAadhaarOtp(
        data?.token,
        data?.wallet_ac_application_number,
        otp
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: result.message || "OTP verification failed",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS" || res.verified === true) {
        Swal.fire({
          icon: "success",
          title: "Aadhaar Verified!",
          text: res.message || "Aadhaar verification successful",
          timer: 2000,
        }).then(() => {
          setShowOtpModal(false);
          navigate("/ppi-money-transfer/verify-pan", {
            state: {
              customerMobile,
              data,
            },
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: res.message || "Aadhaar verification failed",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setVerificationInProgress(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpTimer > 0) {
      Swal.fire({
        icon: "warning",
        title: "Please wait",
        text: `You can resend OTP in ${otpTimer} seconds`,
      });
      return;
    }

    const cleanAadhaar = formData.aadhaar_number.replace(/\s/g, "");
    await generateAadhaarOtp(cleanAadhaar);
  };

  const handleGoBack = () => {
    navigate("/ppi-money-transfer", {
      state: { customerMobile, data },
    });
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Render biometric status
  const renderBiometricStatus = () => {
    if (biometricStatus.loading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h6 className="mb-2">{biometricProgress.message}</h6>
          <ProgressBar
            now={biometricProgress.percentage}
            className="mb-3"
            striped
            animated
          />
          <p className="text-muted small">Step {biometricProgress.step} of 6</p>
        </div>
      );
    }

    if (biometricStatus.error) {
      return (
        <Alert variant="danger" className="text-center">
          <FaExclamationTriangle className="mb-2" size={24} />
          <h6>Biometric Error</h6>
          <p className="mb-3">{biometricStatus.error}</p>
          <div className="d-flex gap-2 justify-content-center">
            {biometricStatus.deviceInfo ? (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRetryBiometric}
              >
                <FaRedo className="me-1" />
                Retry Capture
              </Button>
            ) : (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={checkBiometricDevice}
              >
                <FaRedo className="me-1" />
                Check Device Again
              </Button>
            )}
            <Button
              variant="outline-info"
              size="sm"
              onClick={handleShowTroubleshooting}
            >
              <FaInfoCircle className="me-1" />
              Troubleshoot
            </Button>
          </div>
        </Alert>
      );
    }

    if (biometricStatus.captureData) {
      return (
        <Alert variant="success" className="text-center">
          <FaCheckCircle className="mb-2" size={32} />
          <h6>Biometric Captured Successfully!</h6>
          {/* <div className="mt-3 text-start">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Quality Score:</span>
              <Badge bg="success">
                {biometricStatus.captureData.quality?.score || "N/A"}
              </Badge>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Points Captured:</span>
              <Badge bg="info">
                {biometricStatus.captureData.quality?.nmPoints || "N/A"}
              </Badge>
            </div>
            <div className="d-flex justify-content-between mt-2">
              <span className="text-muted">Device:</span>
              <span className="fw-bold">
                {biometricStatus.captureData.deviceInfo?.dpId || "Unknown"}
              </span>
            </div>
          </div> */}
        </Alert>
      );
    }

    if (biometricStatus.deviceInfo) {
      return (
        <div className="text-center">
          <div className="biometric-animation mb-4">
            <FaFingerprint size={80} className="text-primary" />
            <div className="pulse-ring"></div>
          </div>
          <h5 className="mb-3">Ready to Capture</h5>
          <p className="text-muted mb-4">
            Place your finger on the biometric device when ready
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={handleBiometricCapture}
            className="px-5"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Capturing...
              </>
            ) : (
              <>
                <FaFingerprint className="me-2" />
                Start Capture
              </>
            )}
          </Button>

          <Card className="mt-4">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted d-block">Device:</small>
                  <span className="fw-bold">
                    {biometricStatus.deviceInfo.info}
                  </span>
                </div>
                <Badge bg="success">
                  <FaPlug className="me-1" />
                  Connected
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </div>
      );
    }

    return (
      <div className="text-center py-4">
        <FaFingerprint size={48} className="text-muted mb-3" />
        <h6>Checking Biometric Device...</h6>
        <p className="text-muted small mb-3">
          Please ensure biometric device is connected and RD Service is running
        </p>
        <ProgressBar
          now={biometricProgress.percentage}
          className="mb-3"
          striped
          animated
        />
      </div>
    );
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">
                  <FaIdCard className="me-2" />
                  Verify Aadhaar
                </h5>
                <Button
                  variant="outline-secondary"
                  onClick={handleGoBack}
                  size="sm"
                >
                  <FaArrowLeft className="me-1" />
                  Back
                </Button>
              </div>

              <Form onSubmit={handleVerifyAadhaar}>
                {/* Aadhaar Number Input */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>
                      <FaUser className="me-2" />
                      Aadhaar Number
                    </strong>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaIdCard />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="aadhaar_number"
                      placeholder="Enter 12-digit Aadhaar number"
                      value={formData.aadhaar_number}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_number}
                      maxLength="14"
                      className="py-2"
                    />
                  </InputGroup>
                  {errors.aadhaar_number && (
                    <Form.Text className="text-danger">
                      {errors.aadhaar_number}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Enter your 12-digit Aadhaar number (e.g., 1234 5678 9012)
                  </Form.Text>
                </Form.Group>

                {/* Verification Type Selection */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong className="fs-6">Select Verification Method</strong>
                  </Form.Label>
                  <div className="verification-method-container">
                    <Row className="g-3">
                      <Col md={6}>
                        <div
                          className={`verification-option-card p-4 rounded-3 border cursor-pointer h-100 ${
                            formData.verification_type === "otp"
                              ? "border-primary bg-primary-light"
                              : "border-gray-300"
                          }`}
                          onClick={() => handleVerificationTypeChange("otp")}
                          style={{
                            backgroundColor:
                              formData.verification_type === "otp"
                                ? "#f0f8ff"
                                : "transparent",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-start mb-3">
                            <div className="verification-icon me-3">
                              <div
                                className={`rounded-circle p-3 ${
                                  formData.verification_type === "otp"
                                    ? "bg-primary text-white"
                                    : "bg-light text-secondary"
                                }`}
                              >
                                <FaKey size={20} />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-1 fw-bold">Aadhaar OTP</h6>
                                {formData.verification_type === "otp" && (
                                  <FaCheckCircle className="text-primary" />
                                )}
                              </div>
                              <p className="text-muted mb-2 small">
                                OTP will be sent to your registered mobile
                                number
                              </p>
                            </div>
                          </div>
                          <div className="verification-details">
                            <ul className="list-unstyled mb-0 small text-muted">
                              <li className="mb-1">
                                <FaCheckCircle
                                  className="text-success me-2"
                                  size={12}
                                />
                                Quick and easy verification
                              </li>
                              <li className="mb-1">
                                <FaCheckCircle
                                  className="text-success me-2"
                                  size={12}
                                />
                                No additional hardware required
                              </li>
                              <li>
                                <FaCheckCircle
                                  className="text-success me-2"
                                  size={12}
                                />
                                Recommended for most users
                              </li>
                            </ul>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div
                          className={`verification-option-card p-4 rounded-3 border cursor-pointer h-100 ${
                            formData.verification_type === "biometric"
                              ? "border-primary bg-primary-light"
                              : "border-gray-300"
                          }`}
                          onClick={() =>
                            handleVerificationTypeChange("biometric")
                          }
                          style={{
                            backgroundColor:
                              formData.verification_type === "biometric"
                                ? "#f0f8ff"
                                : "transparent",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-start mb-3">
                            <div className="verification-icon me-3">
                              <div
                                className={`rounded-circle p-3 ${
                                  formData.verification_type === "biometric"
                                    ? "bg-primary text-white"
                                    : "bg-light text-secondary"
                                }`}
                              >
                                <FaFingerprint size={20} />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-1 fw-bold">Biometric</h6>
                                {formData.verification_type === "biometric" && (
                                  <FaCheckCircle className="text-primary" />
                                )}
                              </div>
                              <p className="text-muted mb-2 small">
                                Fingerprint authentication using L1 device
                              </p>
                            </div>
                          </div>
                          <div className="verification-details">
                            <ul className="list-unstyled mb-0 small text-muted">
                              <li className="mb-1">
                                <FaCheckCircle
                                  className="text-success me-2"
                                  size={12}
                                />
                                Highly secure authentication
                              </li>
                              <li className="mb-1">
                                <FaCheckCircle
                                  className="text-success me-2"
                                  size={12}
                                />
                                Instant verification
                              </li>
                              <li>
                                <FaExclamationTriangle
                                  className="text-warning me-2"
                                  size={12}
                                />
                                Requires L1 biometric device
                              </li>
                            </ul>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Form.Group>

                {/* Aadhaar Consent Checkbox */}
                <Form.Group className="mb-4">
                  <div className="border rounded-3 p-3 bg-light">
                    <Form.Check
                      type="checkbox"
                      id="aadhaar-consent"
                      label={
                        <span className="d-flex align-items-center">
                          <span>
                            I hereby give my consent for Aadhaar verification as
                            per the{" "}
                            <Button
                              variant="link"
                              className="p-0 text-decoration-none fw-bold"
                              onClick={handleShowConsent}
                            >
                              Aadhaar Consent
                            </Button>
                          </span>
                        </span>
                      }
                      name="aadhaar_consent"
                      checked={formData.aadhaar_consent}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_consent}
                      className="fw-medium"
                    />
                    {errors.aadhaar_consent && (
                      <Alert variant="danger" className="mt-2 py-2">
                        <FaExclamationTriangle className="me-2" />
                        {errors.aadhaar_consent}
                      </Alert>
                    )}
                  </div>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="py-3 fw-bold rounded-3 shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaShieldAlt className="me-2" />
                        Verify Aadhaar
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Row>

      {/* Aadhaar Consent Modal */}
      <Modal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2" />
            Aadhaar Consent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {consentLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading consent ...</p>
            </div>
          ) : (
            <div className="consent-content">
              <Alert variant="warning" className="mb-3">
                <FaExclamationTriangle className="me-2" />
                Important: Please read the following consent carefully
              </Alert>
              <div className="border p-3 bg-light rounded">
                <p style={{ whiteSpace: "pre-wrap" }}>{aadhaarConsent}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConsentModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setFormData((prev) => ({ ...prev, aadhaar_consent: true }));
              setShowConsentModal(false);
            }}
          >
            <FaCheckCircle className="me-1" />I Agree
          </Button>
        </Modal.Footer>
      </Modal>

      {/* OTP Verification Modal */}
      <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaKey className="me-2" />
            Verify Aadhaar OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <p className="mb-1">
              OTP has been sent to the mobile number registered with Aadhaar
            </p>
            <p className="mb-0 small">Aadhaar: {formData.aadhaar_number}</p>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Enter 6-digit OTP</strong>
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaKey />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").substring(0, 6))
                }
                maxLength={6}
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Enter the OTP received on your registered mobile
            </Form.Text>
          </Form.Group>

          {otpTimer > 0 && (
            <div className="text-center mb-3">
              <p className="mb-0">
                Time remaining:{" "}
                <span className="text-danger fw-bold">
                  {formatTime(otpTimer)}
                </span>
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleResendOtp}
            disabled={otpTimer > 0}
          >
            {otpTimer > 0 ? `Resend (${formatTime(otpTimer)})` : "Resend OTP"}
          </Button>
          <Button
            variant="primary"
            onClick={verifyAadhaarOtp}
            disabled={verificationInProgress || !otp || otp.length !== 6}
          >
            {verificationInProgress ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Biometric Capture Modal */}
      <Modal
        show={showBiometricModal}
        onHide={() => {
          setShowBiometricModal(false);
          resetBiometric();
        }}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFingerprint className="me-2" />
            Biometric Authentication
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderBiometricStatus()}</Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button
            variant="link"
            onClick={handleShowTroubleshooting}
            className="text-decoration-none"
          >
            <FaInfoCircle className="me-1" />
            Troubleshooting Guide
          </Button>
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                setShowBiometricModal(false);
                resetBiometric();
              }}
              className="me-2"
            >
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Troubleshooting Modal */}
      <Modal
        show={showTroubleshoot}
        onHide={() => setShowTroubleshoot(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            Biometric Device Troubleshooting
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <h6>Common Issues & Solutions</h6>
          </Alert>

          <ListGroup variant="flush" className="mb-4">
            {BiometricService.getTroubleshootingGuide().commonIssues.map(
              (issue, index) => (
                <ListGroup.Item key={index}>
                  <div className="d-flex">
                    <Badge bg="warning" className="me-3">
                      {index + 1}
                    </Badge>
                    <div>
                      <strong>{issue.issue}</strong>
                      <p className="mb-0 text-muted">{issue.solution}</p>
                    </div>
                  </div>
                </ListGroup.Item>
              )
            )}
          </ListGroup>

          <h6 className="mb-3">Download RD Service</h6>
          <div className="d-flex flex-wrap gap-2 mb-4">
            {Object.entries(
              BiometricService.getTroubleshootingGuide().downloadLinks
            ).map(([brand, url]) => (
              <a
                key={brand}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                <FaDownload className="me-1" />
                {brand}
              </a>
            ))}
          </div>

          <h6 className="mb-3">Supported L1 Devices</h6>
          <div className="d-flex flex-wrap gap-2">
            {BiometricService.getTroubleshootingGuide().supportedDevices.map(
              (device) => (
                <Badge key={device} bg="light" text="dark" className="border">
                  {device}
                </Badge>
              )
            )}
          </div>
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .biometric-animation {
          position: relative;
          display: inline-block;
        }

        .pulse-ring {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: 4px solid rgba(13, 110, 253, 0.4);
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            transform: scale(0.95);
            opacity: 0.7;
          }
          70% {
            transform: scale(1.1);
            opacity: 0;
          }
          100% {
            transform: scale(0.95);
            opacity: 0;
          }
        }
      `}</style>
    </Container>
  );
};

export default VerifyAadhaar;
